import React, { useEffect, useState, useCallback, useMemo } from "react";
import { FaClock, FaSpinner, FaSearch, FaList } from "react-icons/fa";
import { Link } from "react-router-dom";
import Message from "./Message";

const API_URL = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;

export default function WaitingPayments() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // New state to track if we are looking at INFLOW, OUTFLOW, or ALL
  const [viewMode, setViewMode] = useState("ALL"); 

  const todayStr = new Date().toISOString().split("T")[0];
  
  const fetchWaitingTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API_URL);
      const data = await res.json();
      const pending = (data.transactions || []).filter(
        (tx) => tx.status === "WAITING" && tx.flowRole === "RECEIVER",
      );
      setTransactions(pending);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWaitingTransactions();
  }, [fetchWaitingTransactions]);

  const waitingSummary = useMemo(() => {
    let opening = 0;
    let today = 0;
    let totalIn = 0;   
    let totalOut = 0;  

    transactions.forEach((tx) => {
      const txDate = new Date(tx.timestamp).toISOString().split("T")[0];
      const amount = Number(tx.amount || 0);

      if (txDate < todayStr) opening += amount;
      else if (txDate === todayStr) today += amount;

      if (["IN", "BTIN", "SPLITIN"].includes(tx.paymentType)) {
        totalIn += amount;
      } else if (["OUT", "BTOUT", "SPLITOUT"].includes(tx.paymentType)) {
        totalOut += amount;
      }
    });

    return { opening, today, totalIn, totalOut, closing: opening + today };
  }, [transactions, todayStr]);

  const filteredData = useMemo(() => {
    return transactions
      .filter((tx) => {
        // 1. Search Filter
        const matchesSearch = 
          tx.fromBranch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.toBranch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.senderName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tx.paymentId?.toLowerCase().includes(searchTerm.toLowerCase());

        // 2. View Mode Filter (Inflow vs Outflow)
        let matchesView = true;
        if (viewMode === "INFLOW") {
          matchesView = ["IN", "BTIN", "SPLITIN"].includes(tx.paymentType);
        } else if (viewMode === "OUTFLOW") {
          matchesView = ["OUT", "BTOUT", "SPLITOUT"].includes(tx.paymentType);
        }

        return matchesSearch && matchesView;
      })
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }, [transactions, searchTerm, viewMode]);

  const cardStyle = (borderColor, isActive) => ({
    flex: "1 1 200px",
    padding: "15px",
    background: "#fff",
    borderRadius: "8px",
    boxShadow: isActive ? "0 4px 12px rgba(0,0,0,0.15)" : "0 2px 4px rgba(0,0,0,0.05)",
    borderLeft: `5px solid ${borderColor}`,
    cursor: "pointer",
    transition: "all 0.2s ease",
    transform: isActive ? "scale(1.02)" : "scale(1)",
    opacity: (viewMode === "ALL" || isActive) ? 1 : 0.6
  });

  const labelStyle = { fontSize: "0.7rem", color: "#64748b", fontWeight: "bold", textTransform: "uppercase" };
  const valueStyle = { margin: "5px 0", color: "#1e293b", fontSize: "1.4rem" };

  return (
    <div className="admin-container">
      <header className="admin-header">
        <h1><FaClock /> Waiting Payments</h1>
        <div className="admin-header-buttons">
          <Link to="/"><button className="Branch-nav">Back to Admin</button></Link>
          <button onClick={fetchWaitingTransactions} className="btn-refresh" disabled={loading}>
            <FaSpinner className={loading ? "spin" : ""} /> Refresh
          </button>
        </div>
      </header>

      <section className="waiting-summary-cards" style={{ display: "flex", gap: "15px", padding: "20px", background: "#f8fafc", borderBottom: "1px solid #e2e8f0", flexWrap: "wrap" }}>
        
        {/* INFLOW CARD */}
        <div 
          className="summary-card" 
          style={cardStyle("#22c55e", viewMode === "INFLOW")}
          onClick={() => setViewMode(viewMode === "INFLOW" ? "ALL" : "INFLOW")}
        >
          <span style={labelStyle}>TOTAL INFLOW (Click to Filter)</span>
          <h2 style={valueStyle}>₹{waitingSummary.totalIn.toLocaleString()}</h2>
          {viewMode === "INFLOW" && <small style={{color: "#22c55e"}}>● Filtering Active</small>}
        </div>

        {/* OUTFLOW CARD */}
        <div 
          className="summary-card" 
          style={cardStyle("#ef4444", viewMode === "OUTFLOW")}
          onClick={() => setViewMode(viewMode === "OUTFLOW" ? "ALL" : "OUTFLOW")}
        >
          <span style={labelStyle}>TOTAL OUTFLOW (Click to Filter)</span>
          <h2 style={valueStyle}>₹{waitingSummary.totalOut.toLocaleString()}</h2>
          {viewMode === "OUTFLOW" && <small style={{color: "#ef4444"}}>● Filtering Active</small>}
        </div>
      </section>

      <main className="admin-content">
        <div className="admin-filter-tabs" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', marginTop: '20px', }}>
          <div className="filter-left">
            <FaSearch />
            <input
              className="admin-search-input"
              placeholder="Search Pending..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ marginLeft: "20px", width: "250px" }}
            />
          </div>
          
          {viewMode !== "ALL" && (
            <button 
              onClick={() => setViewMode("ALL")}
              style={{ padding: "5px 15px", borderRadius: "20px", border: "1px solid #cbd5e1", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: "5px" }}
            >
              <FaList size={12}/> Show All Transactions
            </button>
          )}
        </div>

        <div className="table-responsive">
          <table className="branch-table">
            <thead>
              <tr>
                <th>TYPE</th>
                <th>FROM</th>
                <th>TO</th>
                <th>SENDER / RECEIVER</th>
                <th>AMOUNT</th>
                <th>DATE</th>
                <th>ID</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((tx) => (
                  <tr key={tx.paymentId}>
                    <td><strong className={tx.paymentType}>{tx.paymentType}</strong></td>
                    <td>{tx.fromBranch}</td>
                    <td>{tx.toBranch}</td>
                    <td>{tx.senderName} ➔ {tx.receiverName}</td>
                    <td><strong>₹{tx.amount.toLocaleString()}</strong></td>
                    <td>{tx.timestamp.split("T")[0]}</td>
                    <td>{tx.paymentId}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>No transactions found for this view.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
      <Message />
    </div>
  );
}